import { genAttributes, genDay, genRecommendations, scheduleResource } from '@onward-delivery/core';

import { asDateInTZ } from '@/utilities/convertToISO';
import { dateKeyInTZ, parseDateKey, shiftDateKey } from '@onward-delivery/design/calendar-utils';

// Day-eligibility comes from core so this page and the Plan page can't diverge.
export { genDay, genRecommendations, scheduleResource };

// date and dow come from the key itself, not from a shifted Date's accessors — reading getUTCDay()
// off an instant already moved by the zone offset is only correct for negative offsets.
//
// `end` is the next key's business midnight, not start + 1 day: date-fns adds a day in the browser's
// zone, so with the browser outside the business zone a DST day came out an hour long or short and
// a lock written from it spilled into the next day.
export const coreDay = (dateKey, tz) => {
    const { day } = parseDateKey(dateKey);

    return {
        date: day,
        dow: new Date(`${dateKey}T00:00:00.000Z`).getUTCDay(),
        start: asDateInTZ(dateKey, tz).toISOString(),
        end: asDateInTZ(shiftDateKey(dateKey, 1), tz).toISOString(),
    };
};

// How long until the business day rolls over, so a tab left open overnight can move "today" on
// without polling.
export const msUntilNextBusinessDay = (now, tz) =>
    asDateInTZ(shiftDateKey(dateKeyInTZ(now.toISOString(), tz), 1), tz).getTime() - now.getTime();

// `resources.equipment` is a jsonb map of `{key: boolean}`, matching orders' equipment columns.
// The selects work in arrays of keys, so translate at the boundary. Unchecked equipment is left out
// of the map rather than stored as `false` — a resource has nothing to negate, unlike
// `orders.equipment_overrides`, where an explicit `false` cancels a recommendation.
export const equipmentKeys = (equipment) => Object.keys(equipment || {}).filter((key) => equipment[key]);

export const toEquipmentMap = (keys) => Object.fromEntries((keys || []).map((key) => [key, true]));

// Not core's version: this reads `resource.zips`, synthesized by the Apollo cache typePolicy.
export const genResources = ({ client, order }) => {
    const { zip } = genAttributes(order);

    return (client?.available_resources || []).reduce(
        ({ default: defaults, override }, resource) => {
            if (!resource.zips.includes(order[zip])) {
                return { default: defaults, override };
            }

            if (
                resource.shipping_partners.length === 0 ||
                resource.shipping_partners.every((shipping_partner) => !shipping_partner.partner_client_id)
            ) {
                defaults.push(resource);
            }

            if (
                resource.shipping_partners.some(
                    (shipping_partner) => shipping_partner.partner_client_id === order.shipper_id
                )
            ) {
                override.push(resource);
            }

            return { default: defaults, override };
        },
        { default: [], override: [] }
    );
};
